import React, { useState, useEffect } from "react";
import { GlassCard } from "../components/ui";
import { btn3d } from "../styles/buttonStyles";

const deletionStatusIcon = {
  pending: "○",
  running: "…",
  done: "✓",
  failed: "!",
  skipped: "–",
};

export default function MyInfoPage({ dm, personal, lang, loggedInUser, savedTrips, reviews, rankingData, setStep, saveProfileToFirestore, requestRequiredConsent, profileLoading, profileSaving, profileSaved, profileError, logout, accountDeletion, accountDeletionProvider, deleteAccount, deletionResumedFromRedirect }) {
  const [showDeletion, setShowDeletion] = useState(false);
  const [confirmationText, setConfirmationText] = useState("");
  const [deletionPassword, setDeletionPassword] = useState("");
  const deletionBusy = accountDeletion.status === "deleting" || accountDeletion.status === "reauthenticating";
  const confirmationMatches = confirmationText.trim() === "회원 탈퇴";
  const credentialsReady = accountDeletionProvider === "google" || deletionPassword.length > 0;
  const openDeletionPanel = () => {
    setConfirmationText("");
    setDeletionPassword("");
    setShowDeletion(true);
  };
  const closeDeletionPanel = () => {
    if (deletionBusy) return;
    setConfirmationText("");
    setDeletionPassword("");
    setShowDeletion(false);
  };
  // Google 재인증에서 돌아오면 탈퇴가 이미 백그라운드로 재개된 상태다.
  // 패널이 닫혀 있으면 진행 상황 블록이 렌더되지 않아 아무 표시 없이 진행된다.
  useEffect(() => {
    if (deletionResumedFromRedirect) setShowDeletion(true);
  }, [deletionResumedFromRedirect]);

  return (
            <div className="animate-in">
              <GlassCard>
                {/* 헤더 */}
                <div style={{ display:"flex", alignItems:"center", gap:14, marginBottom:24 }}>
                  <div className="text-xl" style={{ width:52, height:52, borderRadius:14, background:"linear-gradient(135deg,var(--accent),var(--teal))", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", fontWeight:900, flexShrink:0, boxShadow:dm?"0 4px 18px rgba(129,140,248,0.3)":"0 4px 14px rgba(99,102,241,0.2)" }}>
                    {personal.name ? personal.name.charAt(0) : "👤"}
                  </div>
                  <div style={{ flex:1 }}>
                    <h2 className="text-md" style={{ fontWeight:800, color:"var(--accent)", marginBottom:2 }}>
                      {lang==="ko"?"내 정보":"My Profile"}
                    </h2>
                    <p className="text-xs" style={{ color:dm?"rgba(226,232,240,0.65)":"rgba(51,65,85,0.60)", fontWeight:500 }}>{loggedInUser}</p>
                  </div>
                </div>

                {/* 구분선 */}
                <div style={{ height:1, background:dm?"linear-gradient(90deg,transparent,rgba(129,140,248,0.25),transparent)":"linear-gradient(90deg,transparent,rgba(99,102,241,0.2),transparent)", marginBottom:20 }}/>

                {profileLoading && (
                  <p className="text-sm" style={{ color:"var(--accent)", textAlign:"center", fontWeight:700, marginBottom:14 }}>
                    프로필 정보를 불러오는 중...
                  </p>
                )}

                {/* 정보 그리드 */}
                <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                  {[
                    { icon:"👤", label:lang==="ko"?"닉네임(이름)":"Nickname", value:personal.name || "-" },
                    { icon:"🎂", label:lang==="ko"?"나이":"Age", value:personal.age || "-" },
                    { icon:"⚧", label:lang==="ko"?"성별":"Gender", value:personal.gender || "-" },
                    { icon:"📍", label:lang==="ko"?"거주지":"Location", value:personal.address || "-" },
                    { icon:"🩸", label:lang==="ko"?"혈액형":"Blood Type", value:personal.bloodType || "-" },
                    { icon:"🧠", label:"MBTI", value:personal.mbti || "-" },
                    { icon:"⭐", label:lang==="ko"?"별자리":"Zodiac", value:personal.zodiac || "-" },
                  ].map(({icon, label, value}) => (
                    <div key={label} style={{ display:"flex", alignItems:"center", gap:12, padding:"11px 14px", borderRadius:12, background:dm?"rgba(255,255,255,0.04)":"rgba(248,250,252,0.88)", border:`1px solid ${dm?"rgba(129,140,248,0.10)":"rgba(99,102,241,0.13)"}` }}>
                      <span className="text-base" style={{ width:22, textAlign:"center", flexShrink:0 }}>{icon}</span>
                      <span className="text-xs" style={{ fontWeight:700, color:dm?"rgba(226,232,240,0.58)":"rgba(67,56,202,0.65)", width:76, flexShrink:0, letterSpacing:"0.03em", whiteSpace:"nowrap" }}>{label}</span>
                      <span className="text-sm" style={{ fontWeight:700, color: value==="-"?dm?"rgba(226,232,240,0.32)":"rgba(51,65,85,0.32)":"var(--text)", flex:1 }}>{value}</span>
                    </div>
                  ))}
                </div>

                {/* 여행 통계 */}
                <div style={{ marginTop:18, padding:"14px 16px", borderRadius:14, background:dm?"rgba(129,140,248,0.08)":"rgba(238,242,255,0.9)", border:`1px solid ${dm?"rgba(129,140,248,0.18)":"rgba(99,102,241,0.2)"}` }}>
                  <p className="text-xs" style={{ fontWeight:800, letterSpacing:"0.12em", textTransform:"uppercase", color:"var(--accent)", marginBottom:12 }}>✦ {lang==="ko"?"여행 기록":"Travel Stats"}</p>
                  <div style={{ display:"flex", gap:0 }}>
                    {[
                      { label:lang==="ko"?"저장한 일정":"Saved Trips", value:savedTrips.length },
                      { label:lang==="ko"?"작성한 후기":"Reviews", value:reviews.filter(r=>r.author===personal.name).length },
                      { label:lang==="ko"?"획득 배지":"Badges", value:(rankingData.find(u=>u.name===personal.name)?.badges||[]).length },
                    ].map(({label, value}, i) => (
                      <div key={label} style={{ flex:1, textAlign:"center", borderRight: i<2?`1px solid ${dm?"rgba(129,140,248,0.15)":"rgba(99,102,241,0.15)"}`:undefined, padding:"0 8px" }}>
                        <div className="text-lg" style={{ fontWeight:900, color:"var(--accent)", lineHeight:1.2 }}>{value}</div>
                        <div className="text-xs" style={{ color:dm?"rgba(226,232,240,0.65)":"rgba(71,85,105,0.65)", marginTop:3, fontWeight:600 }}>{label}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* 정보 수정 버튼 */}
                <button onClick={()=>setStep("personal")}
                  className="btn3d text-sm" style={{ ...btn3d("outline", dm), width:"100%", marginTop:14, padding:"12px", minHeight:46, borderRadius:13, fontWeight:800, letterSpacing:"0.04em" }}>
                  ✏️ {lang==="ko"?"정보 수정하기":"Edit Profile"}
                </button>

                {/* 프로필 저장 버튼 */}
                {profileError && (
                  <p role="alert" className="text-sm" style={{ color:dm?"#FF8F80":"#C0392B", marginTop:10, textAlign:"center", fontWeight:700 }}>
                    ⚠️ {profileError}
                  </p>
                )}
                <button onClick={() => requestRequiredConsent("프로필 저장", saveProfileToFirestore)} disabled={profileSaving||profileSaved}
                  className="btn3d text-sm" style={{ ...btn3d(profileSaved ? "success" : "primary", dm), width:"100%", marginTop:8, padding:"12px", minHeight:46, borderRadius:13, fontWeight:800, cursor: profileSaving||profileSaved?"default":"pointer", letterSpacing:"0.04em", opacity: profileSaving ? 0.7 : 1 }}>
                  {profileSaving ? "⏳ 저장 중..." : profileSaved ? "✅ 저장 완료!" : `💾 ${lang==="ko"?"프로필 저장하기":"Save Profile"}`}
                </button>

                {/* 구분선 */}
                <div style={{ height:1, background:dm?"rgba(255,255,255,0.06)":"rgba(99,102,241,0.1)", margin:"18px 0" }}/>

                {/* 로그아웃 버튼 */}
                <button onClick={()=>{ logout(); setStep("intro"); }}
                  className="btn3d text-sm" style={{ ...btn3d("danger", dm), width:"100%", padding:"12px", minHeight:46, borderRadius:13, fontWeight:800, letterSpacing:"0.04em", display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}>
                  🚪 {lang==="ko"?"로그아웃":"Logout"}
                </button>

                <div style={{ height:1, background:dm?"rgba(255,255,255,0.06)":"rgba(99,102,241,0.1)", margin:"18px 0" }}/>

                {!showDeletion ? (
                  <div>
                    <button
                      type="button"
                      onClick={openDeletionPanel}
                      className="btn3d text-sm"
                      style={{ ...btn3d("danger", dm), width:"100%", padding:"12px", minHeight:46, borderRadius:13, fontWeight:800, letterSpacing:"0.04em" }}
                    >
                      회원 탈퇴
                    </button>
                    <p className="text-xs" style={{ marginTop:7, color:"var(--muted)", textAlign:"center", lineHeight:1.5 }}>
                      계정·프로필은 삭제되며 공유 일정과 후기는 작성자 표시 없이 남습니다.
                    </p>
                  </div>
                ) : (
                  <section
                    aria-labelledby="account-deletion-title"
                    style={{
                      padding:"15px 14px",
                      borderRadius:15,
                      background:dm?"rgba(127,29,29,0.16)":"rgba(254,242,242,0.92)",
                      border:`1px solid ${dm?"rgba(248,113,113,0.35)":"rgba(220,38,38,0.25)"}`,
                    }}
                  >
                    <h3 id="account-deletion-title" className="text-base" style={{ color:dm?"#FCA5A5":"#B91C1C", fontWeight:900, marginBottom:7 }}>
                      계정 삭제 및 여행 기록 익명 보존
                    </h3>
                    <p className="text-xs" style={{ color:"var(--text)", lineHeight:1.65, marginBottom:10 }}>
                      탈퇴 후 아래 계정 정보는 복구할 수 없습니다.
                    </p>
                    <ul className="text-xs" style={{ paddingLeft:19, color:"var(--muted)", lineHeight:1.75, marginBottom:13 }}>
                      <li>Firebase 로그인 계정</li>
                      <li>프로필: 이름·나이대·성별·거주지·이메일·MBTI·혈액형·별자리</li>
                      <li>닉네임 점유와 이 기기의 개인 저장 흔적</li>
                    </ul>
                    <div style={{ padding:"10px 11px", borderRadius:11, background:dm?"rgba(99,102,241,0.13)":"rgba(238,242,255,0.92)", border:`1px solid ${dm?"rgba(129,140,248,0.25)":"rgba(99,102,241,0.2)"}`, marginBottom:13 }}>
                      <p className="text-xs" style={{ color:"var(--accent)", fontWeight:900, marginBottom:5 }}>
                        탈퇴 후에도 익명으로 남는 기록
                      </p>
                      <ul className="text-xs" style={{ paddingLeft:18, color:"var(--muted)", lineHeight:1.7, margin:0 }}>
                        <li>커뮤니티에 공유한 일정과 작성한 후기의 본문·평점</li>
                        <li>저장한 여행 일정의 내용·목적지·기간</li>
                      </ul>
                      <p className="text-xs" style={{ color:"var(--muted)", lineHeight:1.6, marginTop:6 }}>
                        작성자 이름은 “탈퇴한 여행자”로 바뀌고 UID·MBTI·혈액형 등 식별 정보는 제거됩니다.
                        보존 기록은 탈퇴 계정에 다시 연결하거나 복구할 수 없으며, <strong>익명화 이후에는 삭제도 불가능합니다.</strong> 일정 본문에는 출발지 등 입력하신 여행 정보가 그대로 포함되어 있고, 후기 본문에 직접 적은 내용도 남습니다.
                      </p>
                    </div>

                    <label htmlFor="account-deletion-confirmation" className="text-xs" style={{ display:"block", color:"var(--text)", fontWeight:800, marginBottom:6 }}>
                      확인을 위해 <strong style={{ color:dm?"#FCA5A5":"#B91C1C" }}>회원 탈퇴</strong>를 입력하세요
                    </label>
                    <input
                      id="account-deletion-confirmation"
                      type="text"
                      value={confirmationText}
                      onChange={event => setConfirmationText(event.target.value)}
                      disabled={deletionBusy}
                      autoComplete="off"
                      maxLength={20}
                      className="text-base"
                      style={{
                        width:"100%",
                        minHeight:48,
                        padding:"12px 13px",
                        borderRadius:12,
                        border:`1.5px solid ${confirmationMatches?"#16A34A":dm?"rgba(248,113,113,0.38)":"rgba(220,38,38,0.28)"}`,
                        background:dm?"rgba(15,23,42,0.72)":"#fff",
                        color:"var(--text)",
                        fontFamily:"inherit",
                        outline:"none",
                        marginBottom:10,
                      }}
                    />

                    {accountDeletionProvider === "password" ? (
                      <>
                        <label htmlFor="account-deletion-password" className="text-xs" style={{ display:"block", color:"var(--text)", fontWeight:800, marginBottom:6 }}>
                          현재 비밀번호로 본인 확인
                        </label>
                        <input
                          id="account-deletion-password"
                          type="password"
                          value={deletionPassword}
                          onChange={event => setDeletionPassword(event.target.value)}
                          disabled={deletionBusy}
                          autoComplete="current-password"
                          className="text-base"
                          style={{
                            width:"100%",
                            minHeight:48,
                            padding:"12px 13px",
                            borderRadius:12,
                            border:`1.5px solid ${dm?"rgba(248,113,113,0.38)":"rgba(220,38,38,0.28)"}`,
                            background:dm?"rgba(15,23,42,0.72)":"#fff",
                            color:"var(--text)",
                            fontFamily:"inherit",
                            outline:"none",
                            marginBottom:10,
                          }}
                        />
                      </>
                    ) : (
                      <p className="text-xs" style={{ padding:"9px 10px", borderRadius:10, background:dm?"rgba(255,255,255,0.05)":"rgba(255,255,255,0.75)", color:"var(--muted)", lineHeight:1.55, marginBottom:10 }}>
                        삭제 시작 시 같은 Google 계정을 다시 선택해 본인 확인합니다. 모바일이나 팝업 차단 환경에서는 Google 페이지에서 확인한 뒤 돌아오면 탈퇴가 자동으로 이어집니다.
                      </p>
                    )}

                    {accountDeletion.status !== "idle" && (
                      <div
                        aria-live="polite"
                        style={{ padding:"10px", borderRadius:11, background:dm?"rgba(15,23,42,0.45)":"rgba(255,255,255,0.78)", marginBottom:10 }}
                      >
                        <p className="text-xs" style={{
                          color:accountDeletion.status === "complete"?"#16A34A":accountDeletion.status === "error" || accountDeletion.status === "requiresReauth"?dm?"#FCA5A5":"#B91C1C":"var(--accent)",
                          fontWeight:850,
                          lineHeight:1.55,
                          marginBottom:8,
                        }}>
                          {accountDeletion.message}
                        </p>
                        <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
                          {accountDeletion.steps.map(step => (
                            <div key={step.key} className="text-xs" style={{ display:"grid", gridTemplateColumns:"18px minmax(0,1fr)", gap:5, color:"var(--muted)", lineHeight:1.45 }}>
                              <span aria-hidden="true" style={{ color:step.status === "done"?"#16A34A":step.status === "failed"?dm?"#FCA5A5":"#B91C1C":"var(--accent)", fontWeight:900 }}>
                                {deletionStatusIcon[step.status] || "○"}
                              </span>
                              <span>
                                <strong style={{ color:"var(--text)" }}>{step.label}</strong>
                                {step.detail ? ` — ${step.detail}` : ""}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div style={{ display:"grid", gridTemplateColumns:"minmax(0,0.75fr) minmax(0,1.25fr)", gap:8 }}>
                      <button
                        type="button"
                        onClick={closeDeletionPanel}
                        disabled={deletionBusy}
                        className="btn3d text-sm"
                        style={{ ...btn3d("outline", dm), minWidth:0, minHeight:46, borderRadius:12, fontWeight:800 }}
                      >
                        취소
                      </button>
                      <button
                        type="button"
                        onClick={async () => {
                          const completed = await deleteAccount(deletionPassword);
                          if (completed) {
                            setDeletionPassword("");
                            setConfirmationText("");
                            setTimeout(() => setStep("intro"), 1200);
                          }
                        }}
                        disabled={!confirmationMatches || !credentialsReady || deletionBusy}
                        className="btn3d text-sm"
                        style={{
                          ...btn3d("danger", dm),
                          minWidth:0,
                          minHeight:46,
                          borderRadius:12,
                          fontWeight:900,
                          opacity:!confirmationMatches || !credentialsReady || deletionBusy ? 0.55 : 1,
                        }}
                      >
                        {accountDeletion.status === "complete"
                          ? "삭제 완료"
                          : accountDeletion.status === "reauthenticating"
                          ? "본인 확인 중..."
                          : accountDeletion.status === "deleting"
                            ? "삭제 진행 중..."
                            : accountDeletion.status === "error" || accountDeletion.status === "requiresReauth"
                              ? "남은 항목 다시 삭제"
                              : "영구 삭제 시작"}
                      </button>
                    </div>
                  </section>
                )}
              </GlassCard>
            </div>
  );
}
